// Thin wrapper around the shared useCatalogCart (see
// src/features/CatalogGrid/hooks/useCatalogCart.js) — supplies
// ProposalSlice's selectors/action instead of PurchaseLinkSlice's (compare
// PurchaseLink's usePurchaseLinkProducts). Two things stay Proposal-only and
// don't belong in the shared hook: the email-lookup recovery effect below,
// and a dynamic clientType (PurchaseLink always passes 'EXISTING' since
// every account reached via "Create Purchase Link" already exists).
import {useCallback, useEffect} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useCatalogCart} from '../../../../../features/CatalogGrid/hooks/useCatalogCart'
import {useUserFeatures} from '../../../../../context/user-features-context'
import {useAuthClient} from '../../../../../utils/use-auth-client'
import {validateEmailWithApi} from '../../../../../accountManagement/features/EmailLookup/EmailLookup.utils'
import {
  selectActiveOption,
  selectAddonGroups,
  selectIsCatalogDataLoaded,
  selectOneTimeFeeDefinitions,
  selectProductConfigInfo,
  selectProposalCountry,
  selectProposalInfo,
  selectRecurlyAccountInfo,
  selectRecurlySubscriptionItems,
  selectSoftwareGroups,
  setBniMktgSvcsEnabled,
  setRecurlyAccountInfo,
  updateOption,
} from '../../../../../app-store/ProposalSlice'

export function useProductsCatalog() {
  const dispatch = useDispatch()
  const authClient = useAuthClient()
  const userFeatures = useUserFeatures()

  const activeOption = useSelector(selectActiveOption)
  const proposalInfo = useSelector(selectProposalInfo)
  const selectedCountry = useSelector(selectProposalCountry)
  const recurlyAccountInfo = useSelector(selectRecurlyAccountInfo)
  const rawAccountItems = useSelector(selectRecurlySubscriptionItems)
  const rawSoftwareGroups = useSelector(selectSoftwareGroups)
  const rawAddonGroups = useSelector(selectAddonGroups)
  const isDataLoaded = useSelector(selectIsCatalogDataLoaded)
  const oneTimeFeeDefinitions = useSelector(selectOneTimeFeeDefinitions)
  const productConfigInfo = useSelector(selectProductConfigInfo)

  // Recovery: opening an existing proposal for editing (Dashboard "Edit")
  // only dispatches setProposalInfo — it never re-fetches the account's
  // Recurly subscriptions, which is otherwise only populated once, during
  // the initial proposal-creation email-lookup step. Without this,
  // allAccountSubs is empty for every reopened proposal, breaking
  // BC5/MC-trial/Bc5newsales account-context rules for accounts that already
  // own plans. Mirrors the same recovery effect in
  // ClientProposalSchedule/index.js.
  useEffect(() => {
    if (!proposalInfo?.clientEmail) return
    if (Object.keys(recurlyAccountInfo ?? {}).length > 0) return
    validateEmailWithApi({
      email: proposalInfo.clientEmail,
      client: authClient,
      flowType: 'Proposal',
    })
      .then(({meta}) => {
        if (meta?.subDetails) dispatch(setRecurlyAccountInfo(meta.subDetails))
      })
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposalInfo?.clientEmail])

  // Enable_BNI_Mktg_Svcs is needed inside a plain Redux reducer
  // (buildItemsFromRecurly, dispatched from setRecurlyAccountInfo/addOption/
  // setOptionItemsFromRecurly), which has no access to this React context —
  // mirror the flag into Redux so those reducers can read it.
  useEffect(() => {
    dispatch(setBniMktgSvcsEnabled(Boolean(userFeatures?.Enable_BNI_Mktg_Svcs)))
  }, [dispatch, userFeatures?.Enable_BNI_Mktg_Svcs])

  const onItemsChange = useCallback(
    items => {
      if (!activeOption) return
      dispatch(
        updateOption({
          displayOrder: activeOption.displayOrder,
          changes: {items},
        }),
      )
    },
    [dispatch, activeOption],
  )

  return useCatalogCart({
    activeItem: activeOption,
    country: selectedCountry,
    recurlyAccountInfo,
    rawAccountItems,
    rawSoftwareGroups,
    rawAddonGroups,
    isDataLoaded,
    oneTimeFeeDefinitions,
    productConfigInfo,
    clientType: proposalInfo?.clientType,
    onItemsChange,
  })
}
